/**
 * Shared Access Store - Zustand State Management
 * 
 * Manages shared access (secondary users) state.
 */

import { create } from 'zustand';
import type {
  SharedAccess,
  CreateSharedAccessRequest,
  UpdateSharedAccessRequest,
} from '@budgira/shared';
import { sharedAccessService } from '../services/shared-access.service';
import { localizeError, mapBackendError } from '../utils/errorMapper';

interface SharedAccessState {
  sharedAccesses: SharedAccess[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchSharedAccesses: () => Promise<void>;
  createSharedAccess: (data: CreateSharedAccessRequest, sendInvitation?: boolean) => Promise<SharedAccess | null>;
  updateSharedAccess: (id: string, data: UpdateSharedAccessRequest) => Promise<SharedAccess | null>;
  blockSharedAccess: (id: string) => Promise<boolean>;
  unblockSharedAccess: (id: string) => Promise<boolean>;
  deleteSharedAccess: (id: string) => Promise<boolean>;
  sendInvitation: (id: string) => Promise<{ success: boolean; message: string }>;
  clearError: () => void;
}

export const useSharedAccessStore = create<SharedAccessState>((set) => ({
  sharedAccesses: [],
  isLoading: false,
  error: null,

  fetchSharedAccesses: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const response = await sharedAccessService.list();
      if (response.success && response.data) {
        set({ sharedAccesses: response.data, isLoading: false });
      } else {
        set({ error: response.error?.message || 'Failed to fetch shared accesses', isLoading: false });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      set({ error: message, isLoading: false });
    }
  },

  createSharedAccess: async (
    data: CreateSharedAccessRequest,
    sendInvitation: boolean = true
  ): Promise<SharedAccess | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await sharedAccessService.create(data, sendInvitation);
      if (response.success && response.data) {
        const newAccess = response.data;
        set((state) => ({
          sharedAccesses: [...state.sharedAccesses, newAccess],
          isLoading: false,
        }));
        return newAccess;
      } else {
        const errorMessage = response.error?.message || 'Failed to create shared access';
        set({ error: mapBackendError(errorMessage), isLoading: false });
        return null;
      }
    } catch (err: unknown) {
      const message = localizeError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  updateSharedAccess: async (
    id: string,
    data: UpdateSharedAccessRequest
  ): Promise<SharedAccess | null> => {
    set({ isLoading: true, error: null });
    try {
      const response = await sharedAccessService.update(id, data);
      if (response.success && response.data) {
        const updatedAccess = response.data;
        set((state) => ({
          sharedAccesses: state.sharedAccesses.map((sa) =>
            sa.id === id ? updatedAccess : sa
          ),
          isLoading: false,
        }));
        return updatedAccess;
      } else {
        set({ error: response.error?.message || 'Failed to update shared access', isLoading: false });
        return null;
      }
    } catch (err: unknown) {
      const message = localizeError(err);
      set({ error: message, isLoading: false });
      return null;
    }
  },

  blockSharedAccess: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      const response = await sharedAccessService.block(id);
      if (response.success && response.data) {
        const updatedAccess = response.data;
        set((state) => ({
          sharedAccesses: state.sharedAccesses.map((sa) =>
            sa.id === id ? updatedAccess : sa
          ),
          isLoading: false,
        }));
        return true;
      } else {
        set({ error: response.error?.message || 'Failed to block access', isLoading: false });
        return false;
      }
    } catch (err: unknown) {
      const message = localizeError(err);
      set({ error: message, isLoading: false });
      return false;
    }
  },

  unblockSharedAccess: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      const response = await sharedAccessService.unblock(id);
      if (response.success && response.data) {
        const updatedAccess = response.data;
        set((state) => ({
          sharedAccesses: state.sharedAccesses.map((sa) =>
            sa.id === id ? updatedAccess : sa
          ),
          isLoading: false,
        }));
        return true;
      } else {
        set({ error: response.error?.message || 'Failed to unblock access', isLoading: false });
        return false;
      }
    } catch (err: unknown) {
      const message = localizeError(err);
      set({ error: message, isLoading: false });
      return false;
    }
  },

  deleteSharedAccess: async (id: string): Promise<boolean> => {
    set({ isLoading: true, error: null });
    try {
      const response = await sharedAccessService.delete(id);
      if (response.success) {
        set((state) => ({
          sharedAccesses: state.sharedAccesses.filter((sa) => sa.id !== id),
          isLoading: false,
        }));
        return true;
      } else {
        set({ error: response.error?.message || 'Failed to delete shared access', isLoading: false });
        return false;
      }
    } catch (err: unknown) {
      const message = localizeError(err);
      set({ error: message, isLoading: false });
      return false;
    }
  },

  sendInvitation: async (id: string): Promise<{ success: boolean; message: string }> => {
    set({ isLoading: true, error: null });
    try {
      const response = await sharedAccessService.sendInvitation(id);
      set({ isLoading: false });
      if (response.success && response.data) {
        return {
          success: response.data.sent,
          message: response.data.message,
        };
      } else {
        const message = response.error?.message || 'Failed to send invitation';
        set({ error: message });
        return { success: false, message };
      }
    } catch (err: unknown) {
      const message = localizeError(err);
      set({ error: message, isLoading: false });
      return { success: false, message };
    }
  },

  clearError: () => set({ error: null }),
}));

export default useSharedAccessStore;
